import React, { useState, useEffect } from 'react';
import { ChevronRight, Home, RefreshCw, X, Loader2, AlertCircle, Download } from 'lucide-react';
import { apiService } from '../../services/api.service';
import {
  DrillDownLevel,
  BeneficiaryData
} from '../../types/drill-down-types';
import { toast } from 'react-hot-toast';

/**
 * DRILL-DOWN TABLE COMPONENT
 * Hierarchical navigation: District → Mandal → Village → Beneficiaries
 * Shows UNIQUE beneficiary counts with reattempt-aware success/failed.
 */

const DrillDownTable: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState<DrillDownLevel>({ type: 'DISTRICT' });
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<DrillDownLevel[]>([{ type: 'DISTRICT' }]);
  const [aggregates, setAggregates] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Never Paid modal state
  const [neverPaidModal, setNeverPaidModal] = useState(false);
  const [neverPaidDistrict, setNeverPaidDistrict] = useState('');
  const [neverPaidData, setNeverPaidData] = useState<any[]>([]);
  const [neverPaidLoading, setNeverPaidLoading] = useState(false);
  const [neverPaidTotal, setNeverPaidTotal] = useState(0);
  const [neverPaidPage, setNeverPaidPage] = useState(1);
  const [neverPaidTotalPages, setNeverPaidTotalPages] = useState(1);
  const [exporting, setExporting] = useState(false);

  const handleNeverPaidClick = async (district: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setNeverPaidDistrict(district);
    setNeverPaidPage(1);
    setNeverPaidModal(true);
    fetchNeverPaid(district, 1);
  };

  const fetchNeverPaid = async (district: string, page: number) => {
    setNeverPaidLoading(true);
    try {
      const res = await apiService.getNeverPaidBeneficiaries(district, { page, limit: 50 });
      setNeverPaidData(res.beneficiaries || []);
      setNeverPaidTotal(res.totalCount || 0);
      setNeverPaidTotalPages(res.pagination?.totalPages || 1);
    } catch (err) {
      toast.error('Failed to load never-paid beneficiaries');
    } finally {
      setNeverPaidLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: { district?: string; mandal?: string; village?: string } = {};
      if (currentLevel.district) params.district = currentLevel.district;
      if (currentLevel.mandal) params.mandal = currentLevel.mandal;
      if (currentLevel.village) params.village = currentLevel.village;

      const blob = await apiService.exportDrillDown(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let fileName = 'All_Districts.xlsx';
      if (currentLevel.type === 'MANDAL') fileName = `Mandals_${currentLevel.district}.xlsx`;
      else if (currentLevel.type === 'VILLAGE') fileName = `Villages_${currentLevel.district}_${currentLevel.mandal}.xlsx`;
      else if (currentLevel.type === 'BENEFICIARY') fileName = `Beneficiaries_${currentLevel.district}_${currentLevel.mandal}_${currentLevel.village}.xlsx`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (neverPaidModal && neverPaidDistrict) {
      fetchNeverPaid(neverPaidDistrict, neverPaidPage);
    }
  }, [neverPaidPage]);

  useEffect(() => {
    fetchData();
  }, [currentLevel, currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let response;

      switch (currentLevel.type) {
        case 'DISTRICT':
          response = await apiService.getDistrictDrillDown();
          setData(response.data || []);
          setAggregates(null);
          break;

        case 'MANDAL':
          response = await apiService.getMandalDrillDown(currentLevel.district!);
          setData(response.data || []);
          setAggregates(null);
          break;

        case 'VILLAGE':
          response = await apiService.getVillageDrillDown(
            currentLevel.district!,
            currentLevel.mandal!
          );
          setData(response.data || []);
          setAggregates(null);
          break;

        case 'BENEFICIARY':
          response = await apiService.getBeneficiaryDrillDown(
            currentLevel.district!,
            currentLevel.mandal!,
            currentLevel.village!,
            { page: currentPage, limit: 50 }
          );
          setData(response.beneficiaries || []);
          setAggregates(response.aggregates || null);

          if (response.pagination) {
            setTotalPages(response.pagination.totalPages);
            setTotalRecords(response.pagination.total);
          }
          break;
      }
    } catch (error: any) {
      console.error('Error fetching drill-down data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDrillDown = (item: any) => {
    let newLevel: DrillDownLevel;

    if (currentLevel.type === 'DISTRICT') {
      newLevel = { type: 'MANDAL', district: item.district };
    } else if (currentLevel.type === 'MANDAL') {
      newLevel = { type: 'VILLAGE', district: currentLevel.district, mandal: item.mandal };
    } else if (currentLevel.type === 'VILLAGE') {
      newLevel = {
        type: 'BENEFICIARY',
        district: currentLevel.district,
        mandal: currentLevel.mandal,
        village: item.village
      };
    } else {
      return;
    }

    setCurrentLevel(newLevel);
    setBreadcrumbs([...breadcrumbs, newLevel]);
    setCurrentPage(1);
  };

  const handleBreadcrumbClick = (index: number) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentLevel(newBreadcrumbs[newBreadcrumbs.length - 1]);
    setCurrentPage(1);
  };

  const getBreadcrumbName = (crumb: DrillDownLevel): string => {
    switch (crumb.type) {
      case 'DISTRICT': return 'All Districts';
      case 'MANDAL': return crumb.district || '';
      case 'VILLAGE': return crumb.mandal || '';
      case 'BENEFICIARY': return crumb.village || '';
      default: return '';
    }
  };

  const formatAmount = (amount: number) => {
    if (!amount || amount === 0) return '₹0.00';
    const crores = amount / 10000000;
    if (crores >= 1) return `₹${crores.toFixed(2)} Cr`;
    const lakhs = amount / 100000;
    return `₹${lakhs.toFixed(2)} L`;
  };

  const renderBreadcrumbs = () => (
    <div className="flex items-center space-x-2 mb-6 text-sm">
      <Home
        className="w-4 h-4 cursor-pointer text-blue-600 hover:text-blue-800 transition-colors"
        onClick={() => handleBreadcrumbClick(0)}
      />
      {breadcrumbs.map((crumb, index) => (
        <React.Fragment key={index}>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span
            className={`cursor-pointer transition-colors ${
              index === breadcrumbs.length - 1
                ? 'text-gray-900 font-semibold'
                : 'text-blue-600 hover:text-blue-800'
            }`}
            onClick={() => handleBreadcrumbClick(index)}
          >
            {getBreadcrumbName(crumb)}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  /**
   * AGGREGATE TABLE - District/Mandal/Village levels
   * Columns: Geo, Beneficiaries, Succeeded, Failed, BL/RL/RC breakdown, Paid Amount
   */
  const renderAggregateTable = () => {
    const geoField = currentLevel.type === 'DISTRICT' ? 'district'
      : currentLevel.type === 'MANDAL' ? 'mandal' : 'village';

    const totals = data.reduce((acc, item) => ({
      totalBeneficiaries: acc.totalBeneficiaries + (item.totalBeneficiaries || 0),
      pennyDropOnly: acc.pennyDropOnly + (item.pennyDropOnly || 0),
      noRecord: acc.noRecord + (item.noRecord || 0),
      successCount: acc.successCount + (item.success?.count || 0),
      failedCount: acc.failedCount + (item.failed?.count || 0),
      successAmount: acc.successAmount + (item.success?.amount || 0),
      blSuccess: acc.blSuccess + (item.stageBreakdown?.bl?.success || 0),
      blFailed: acc.blFailed + (item.stageBreakdown?.bl?.failed || 0),
      rlSuccess: acc.rlSuccess + (item.stageBreakdown?.rl?.success || 0),
      rlFailed: acc.rlFailed + (item.stageBreakdown?.rl?.failed || 0),
      rcSuccess: acc.rcSuccess + (item.stageBreakdown?.rc?.success || 0),
      rcFailed: acc.rcFailed + (item.stageBreakdown?.rc?.failed || 0),
    }), {
      totalBeneficiaries: 0, pennyDropOnly: 0, noRecord: 0, successCount: 0, failedCount: 0, successAmount: 0,
      blSuccess: 0, blFailed: 0, rlSuccess: 0, rlFailed: 0, rcSuccess: 0, rcFailed: 0,
    });

    return (
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {currentLevel.type}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Beneficiaries
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Succeeded
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Failed
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Penny Drop Only
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                BL
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                RL
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                RC
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Paid Amount
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => {
              const name = item[geoField];
              return (
                <tr
                  key={`${name}-${index}`}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleDrillDown(item)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-700">
                    {item.totalBeneficiaries?.toLocaleString('en-IN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-green-600 font-semibold">
                      {item.success?.count?.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-red-600 font-semibold">
                      {item.failed?.count?.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    {(item.pennyDropOnly || 0) > 0 ? (
                      <button
                        onClick={(e) => handleNeverPaidClick(item[geoField], e)}
                        className="text-yellow-600 font-semibold hover:text-blue-600 hover:underline transition-colors"
                      >
                        {(item.pennyDropOnly || 0).toLocaleString('en-IN')}
                      </button>
                    ) : (
                      <span className="text-gray-500 font-semibold">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="text-green-600">{item.stageBreakdown?.bl?.success?.toLocaleString('en-IN')}</div>
                    {(item.stageBreakdown?.bl?.failed || 0) > 0 && (
                      <div className="text-xs text-red-500">{item.stageBreakdown.bl.failed} failed</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="text-green-600">{item.stageBreakdown?.rl?.success?.toLocaleString('en-IN')}</div>
                    {(item.stageBreakdown?.rl?.failed || 0) > 0 && (
                      <div className="text-xs text-red-500">{item.stageBreakdown.rl.failed} failed</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="text-green-600">{item.stageBreakdown?.rc?.success?.toLocaleString('en-IN')}</div>
                    {(item.stageBreakdown?.rc?.failed || 0) > 0 && (
                      <div className="text-xs text-red-500">{item.stageBreakdown.rc.failed} failed</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600">
                    {item.success?.amountFormatted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <ChevronRight className="w-5 h-5 text-blue-600 mx-auto" />
                  </td>
                </tr>
              );
            })}

            {/* TOTALS ROW */}
            {data.length > 0 && (
              <tr className="bg-blue-50 font-bold border-t-2 border-blue-300">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">TOTAL</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  {totals.totalBeneficiaries.toLocaleString('en-IN')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-700">
                  {totals.successCount.toLocaleString('en-IN')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-700">
                  {totals.failedCount.toLocaleString('en-IN')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-yellow-700">
                  {totals.pennyDropOnly.toLocaleString('en-IN')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-700">
                  {totals.blSuccess.toLocaleString('en-IN')}
                  {totals.blFailed > 0 && <div className="text-xs text-red-500">{totals.blFailed} failed</div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-700">
                  {totals.rlSuccess.toLocaleString('en-IN')}
                  {totals.rlFailed > 0 && <div className="text-xs text-red-500">{totals.rlFailed} failed</div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-700">
                  {totals.rcSuccess.toLocaleString('en-IN')}
                  {totals.rcFailed > 0 && <div className="text-xs text-red-500">{totals.rcFailed} failed</div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-blue-700">
                  {formatAmount(totals.successAmount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">-</td>
              </tr>
            )}
          </tbody>
        </table>

        {data.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">No data available</div>
        )}
      </div>
    );
  };

  /**
   * AGGREGATE SUMMARY CARD for beneficiary level
   */
  const renderAggregateSummary = () => {
    if (!aggregates) return null;

    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6 shadow-sm border border-blue-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Village Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total Beneficiaries</p>
            <p className="text-2xl font-bold text-gray-900">
              {(aggregates.totalBeneficiaries || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Succeeded</p>
            <p className="text-2xl font-bold text-green-600">
              {(aggregates.uniqueSuccessBens || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Failed</p>
            <p className="text-2xl font-bold text-red-600">
              {(aggregates.uniqueFailedBens || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Paid Amount</p>
            <p className="text-2xl font-bold text-blue-600">
              {aggregates.successAmountFormatted || '₹0.00'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  /**
   * BENEFICIARY TABLE
   */
  const renderBeneficiaryTable = () => (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Beneficiary ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Stage
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Paid Amount
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Attempts
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((beneficiary: BeneficiaryData, index) => (
            <tr key={beneficiary.id || index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {beneficiary.beneficiaryId}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {beneficiary.applicantName}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  {beneficiary.stage}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                {beneficiary.amountFormatted ||
                  new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(beneficiary.amountInRupees)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-700">
                {beneficiary.totalTransactions}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                {beneficiary.bankStatus === 'Success' && (
                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Success
                  </span>
                )}
                {beneficiary.bankStatus === 'Failed' && (
                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    Failed
                  </span>
                )}
                {beneficiary.bankStatus === 'Not Presented' && (
                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                    Not Presented
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * 50 + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * 50, totalRecords)}</span> of{' '}
                <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {data.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">No beneficiaries found</div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Geographic Payment Report</h1>
            <p className="mt-1 text-sm text-gray-600">
              District → Mandal → Village → Beneficiaries (unique counts, reattempt-aware)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              disabled={exporting || loading || data.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {renderBreadcrumbs()}

        {currentLevel.type === 'BENEFICIARY' && renderAggregateSummary()}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : currentLevel.type === 'BENEFICIARY' ? (
          renderBeneficiaryTable()
        ) : (
          renderAggregateTable()
        )}
      </div>

      {/* Never Paid Modal */}
      {neverPaidModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setNeverPaidModal(false)} />
          <div className="fixed inset-0 overflow-hidden">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div>
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-gray-600" />
                      <h2 className="text-xl font-bold text-gray-900">Penny Drop Only — No Payment Attempted</h2>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {neverPaidDistrict} — {neverPaidTotal.toLocaleString('en-IN')} beneficiaries
                    </p>
                  </div>
                  <button onClick={() => setNeverPaidModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {neverPaidLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beneficiary ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mandal</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Village</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Penny Drop</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Attempts</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {neverPaidData.map((b, idx) => (
                            <tr key={`${b.beneficiaryId}-${idx}`} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{b.beneficiaryId}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{b.applicantName}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{b.mandal}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{b.village}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {b.stage || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-center">
                                {b.pennyDropStatus === 'SUCCESS' ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Done</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-center text-gray-700">{b.totalAttempts}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  b.reason === 'All Attempts Failed'
                                    ? 'bg-red-100 text-red-800'
                                    : b.reason === 'Penny Drop Only'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {b.reason}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {neverPaidData.length === 0 && (
                        <div className="text-center py-8 text-gray-500">No beneficiaries found</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {neverPaidTotalPages > 1 && (
                  <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      Page {neverPaidPage} of {neverPaidTotalPages} ({neverPaidTotal.toLocaleString('en-IN')} total)
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setNeverPaidPage(p => Math.max(1, p - 1))}
                        disabled={neverPaidPage === 1}
                        className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setNeverPaidPage(p => Math.min(neverPaidTotalPages, p + 1))}
                        disabled={neverPaidPage === neverPaidTotalPages}
                        className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrillDownTable;
